import { isEmpty, isArray } from 'lodash'
import React, { Children, cloneElement } from 'react'
import classnames from 'classnames/bind'
import RadioItem from '../radioField'
import { formFieldWrapper } from '../formFieldWrapper'
import styles from './styles.scss'
import RadioButtonField, {
  RadioButtonFieldType,
} from '../radioField/radioButtonField'
const cx = classnames.bind(styles)

export enum BaseRadioGroupFieldVariant {
  DEFAULT = 'default',
  INLINE = 'inline',
  TWO_COLUMN = 'twoColumn',
}

export type BaseRadioGroupFieldProps = {
  options?: Array<{
    elmId?: string
    label: string
    value: string | number
    disabled?: boolean
  }>
  elmId?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  showError?: boolean
  size?: string // deprecated but here for backward compatibility
  compact?: boolean
  value?: string | number
  children?: JSX.Element[]
  disabled?: boolean
  className?: string
  inline?: boolean // deprecated: use variant = BaseRadioGroupFieldVariant.INLINE instead
  secondary?: boolean
  variant?: BaseRadioGroupFieldVariant
}

const BaseRadioGroupField = ({
  options,
  elmId,
  onChange,
  showError, // eslint-disable-line
  size, // deprecated but here for backward compatibility
  compact,
  value,
  children,
  disabled,
  className,
  inline,
  variant,
  secondary,
  ...rest
}: BaseRadioGroupFieldProps): JSX.Element => {
  const newCompact = size === 'small' ? true : compact

  const formProps = {
    onChange,
    className: cx(className, 'radio-group', { medium: size === 'medium' }),
  }

  const childrenOptions = children ? (
    Children.map(children, (child, index) => {
      return cloneElement(child, {
        ...formProps,
        ...rest,
        disabled: child?.props?.disabled || disabled,
        checked: value === child?.props?.value,
        compact: newCompact,
        secondary,
        elmId: child?.props?.elmId || `${elmId}_radio_option_${index}`,
      })
    })
  ) : (
    <div />
  )

  let formattedChildrenOptions
  switch (variant) {
    case BaseRadioGroupFieldVariant.INLINE:
      formattedChildrenOptions = (
        <div className={cx('inline-options')}>{childrenOptions}</div>
      )
      break
    case BaseRadioGroupFieldVariant.TWO_COLUMN:
      formattedChildrenOptions = (
        <div className={cx('two-column-options')}>{childrenOptions}</div>
      )
      break
    default: {
      formattedChildrenOptions = inline ? (
        <div className={cx('inline-options')}>{childrenOptions}</div>
      ) : (
        childrenOptions
      )
    }
  }

  return (
    <>
      {!isArray(options) || isEmpty(options)
        ? formattedChildrenOptions
        : options.map((option, index) => {
            return (
              <RadioItem
                key={`options_${index}`}
                data-elm-id={option.elmId || `${elmId}_radio_option_${index}`}
                label={option.label}
                onChange={onChange}
                value={option.value || ''}
                checked={
                  value === option.value ||
                  (isEmpty(value) && isEmpty(option.value))
                }
                disabled={disabled || option.disabled}
                className={cx('radio-group', { medium: size === 'medium' })}
                compact={newCompact}
                {...rest}
              />
            )
          })}
    </>
  )
}

const RadioGroupField = formFieldWrapper(
  (fieldProps: BaseRadioGroupFieldProps, showError: boolean) => (
    <BaseRadioGroupField {...fieldProps} showError={showError} />
  )
)

export const RadioButtonGroupField = ({
  options,
  disabled,
  inline,
  elmId,
  name,
  ...rest
}: {
  options: RadioButtonFieldType[]
  disabled?: boolean
  inline?: boolean
  elmId: string
  name: string
  value?: any
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    { value }: { value: string }
  ) => void
}) => {
  return (
    <RadioGroupField
      elmId={elmId}
      disabled={disabled}
      name={name}
      inline={inline}
      {...rest}
    >
      {options.map((option: RadioButtonFieldType, index: number) => (
        <RadioButtonField
          {...option}
          disabled={disabled || option?.disabled}
          key={index}
        />
      ))}
    </RadioGroupField>
  )
}

export default RadioGroupField
